/*
 * Copyright 2025 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
package analytics

import kotlinx.datetime.*
import kotlin.math.roundToInt

/**
 * Shared analytics module for calculating monthly savings percentages
 * across iOS, Android, and Web platforms.
 */
object MonthlySavingsAnalytics {

    /**
     * Data class representing financial data for a single month
     */
    data class MonthlyFinanceData(
        val month: Int,
        val year: Int,
        val confirmedIncome: Double,
        val realizedExpenses: Double,
        val expectedIncome: Double = 0.0,
        val plannedExpenses: Double = 0.0,
        val goalAllocations: Double = 0.0,
        /** Server-supplied smoothed monthly income (annual estimate ÷ 12). When
         *  present it's the savings-rate denominator; null falls back to the
         *  month's actual income. */
        val smoothedMonthlyIncome: Double? = null
    ) {
        /** Actual savings = income - expenses (kept for net money display and portfolio optimizer) */
        val actualSavings: Double
            get() = confirmedIncome - realizedExpenses

        /** Canonical savings percentage — see [savingsRatePercent]. */
        val savingsPercentage: Double
            get() = savingsRatePercent(goalAllocations, smoothedMonthlyIncome, confirmedIncome)

        /** Whether this month has any transaction data */
        val hasData: Boolean
            get() = confirmedIncome > 0 || realizedExpenses > 0

        /** Total income including expected/planned */
        val totalIncome: Double
            get() = confirmedIncome + expectedIncome

        /** Total expenses including planned */
        val totalExpenses: Double
            get() = realizedExpenses + plannedExpenses

        /** Rounded savings percentage for display */
        val savingsPercentageRounded: Int
            get() = savingsPercentage.roundToInt()
    }

    /**
     * Calculate monthly finance data from transactions for a given year
     *
     * @param transactions List of Transaction objects
     * @param year The year to calculate data for
     * @return Map of month number (1-12) to MonthlyFinanceData
     */
    fun calculateMonthlyData(
        transactions: List<models.Transaction>,
        year: Int,
        goalAllocationsByMonth: Map<Int, Double> = emptyMap(),
        smoothedMonthlyIncome: Double? = null
    ): Map<Int, MonthlyFinanceData> {
        // Group transactions by month
        val monthlyIncome = mutableMapOf<Int, Double>()
        val monthlyExpenses = mutableMapOf<Int, Double>()

        // Filter out parent transactions (split/distributed parents)
        // Only their children should count towards totals
        val effectiveTransactions = transactions.filter { !it.excludeFromTotals }

        effectiveTransactions.forEach { transaction ->
            try {
                val date = LocalDate.parse(transaction.date)
                if (date.year == year) {
                    val month = date.monthNumber
                    if (transaction.amount > 0) {
                        // Positive amount = income
                        monthlyIncome[month] = (monthlyIncome[month] ?: 0.0) + transaction.amount
                    } else {
                        // Negative amount = expense (store as positive)
                        monthlyExpenses[month] = (monthlyExpenses[month] ?: 0.0) + kotlin.math.abs(transaction.amount)
                    }
                }
            } catch (e: Exception) {
                // Skip transactions with invalid dates
            }
        }

        // Build map for all 12 months
        return (1..12).associateWith { month ->
            MonthlyFinanceData(
                month = month,
                year = year,
                confirmedIncome = monthlyIncome[month] ?: 0.0,
                realizedExpenses = monthlyExpenses[month] ?: 0.0,
                goalAllocations = goalAllocationsByMonth[month] ?: 0.0,
                smoothedMonthlyIncome = smoothedMonthlyIncome
            )
        }
    }

    /**
     * Determine which months to display in charts based on data availability
     *
     * For past years: Show from first month with data to December
     * For current year: Show January through current month + 1 (forecast)
     * For future years: Show from first planned month to December
     *
     * @param data Map of monthly data
     * @param year Year being displayed
     * @param currentYear Current calendar year
     * @param currentMonth Current calendar month (1-12)
     * @return List of month numbers to display
     */
    fun getMonthsToDisplay(
        data: Map<Int, MonthlyFinanceData>,
        year: Int,
        currentYear: Int,
        currentMonth: Int
    ): List<Int> {
        return when {
            year < currentYear -> {
                // Past year: from first month with data to December
                val firstMonthWithData = (1..12).firstOrNull { data[it]?.hasData == true } ?: 1
                (firstMonthWithData..12).toList()
            }
            year == currentYear -> {
                // Current year: January through current month + 1 (for forecast)
                (1..minOf(12, currentMonth + 1)).toList()
            }
            else -> {
                // Future year: show all planned months
                val firstPlanned = (1..12).firstOrNull {
                    val monthData = data[it]
                    monthData != null && (monthData.expectedIncome > 0 || monthData.plannedExpenses > 0)
                } ?: 1
                (firstPlanned..12).toList()
            }
        }
    }

    /**
     * Calculate year-to-date savings percentage
     */
    fun calculateYTDSavingsPercentage(data: Map<Int, MonthlyFinanceData>): Double {
        val monthsWithData = data.values.filter { it.hasData }
        if (monthsWithData.isEmpty()) return 0.0

        val totalIncome = monthsWithData.sumOf { it.confirmedIncome }
        val totalGoalAllocations = monthsWithData.sumOf { it.goalAllocations }

        return if (totalIncome > 0) {
            (totalGoalAllocations / totalIncome * 100)
        } else {
            0.0
        }
    }

    /**
     * Generate savings recommendation based on current data
     */
    fun generateSavingsRecommendation(
        data: Map<Int, MonthlyFinanceData>,
        targetSavingsRate: Double = 20.0
    ): SavingsRecommendation {
        val monthsWithData = data.values.filter { it.hasData }
        if (monthsWithData.isEmpty()) {
            return SavingsRecommendation(
                message = "No transaction data available yet.",
                currentRate = 0.0,
                targetRate = targetSavingsRate,
                difference = targetSavingsRate,
                isOnTrack = false
            )
        }

        val totalIncome = monthsWithData.sumOf { it.confirmedIncome }
        val totalGoalAllocations = monthsWithData.sumOf { it.goalAllocations }
        val actualRate = if (totalIncome > 0) (totalGoalAllocations / totalIncome * 100) else 0.0
        val difference = targetSavingsRate - actualRate
        val isOnTrack = actualRate >= targetSavingsRate

        val message = when {
            actualRate >= targetSavingsRate + 10 ->
                "Excellent! You're saving ${actualRate.roundToInt()}%, well above your ${targetSavingsRate.roundToInt()}% target."
            actualRate >= targetSavingsRate ->
                "Great job! You're on track with ${actualRate.roundToInt()}% savings rate."
            actualRate >= targetSavingsRate - 5 ->
                "Almost there! You're at ${actualRate.roundToInt()}%, just ${difference.roundToInt()}% below target."
            actualRate > 0 ->
                "You're saving ${actualRate.roundToInt()}%. Try to reduce expenses by $${(difference * totalIncome / 100).roundToInt()} to hit your target."
            else ->
                "Your expenses exceed income. Consider reviewing your spending habits."
        }

        return SavingsRecommendation(
            message = message,
            currentRate = actualRate,
            targetRate = targetSavingsRate,
            difference = difference,
            isOnTrack = isOnTrack,
            suggestedMonthlySavings = if (totalIncome > 0) (totalIncome * targetSavingsRate / 100 / monthsWithData.size) else 0.0
        )
    }

    /**
     * Get month name abbreviation
     */
    fun getMonthAbbreviation(month: Int): String {
        return when (month) {
            1 -> "Jan"
            2 -> "Feb"
            3 -> "Mar"
            4 -> "Apr"
            5 -> "May"
            6 -> "Jun"
            7 -> "Jul"
            8 -> "Aug"
            9 -> "Sep"
            10 -> "Oct"
            11 -> "Nov"
            12 -> "Dec"
            else -> ""
        }
    }
}

/**
 * Canonical savings rate — the SINGLE source every surface uses (dashboard
 * number, chart bars, tooltip, trend line, and the server insight): goal
 * allocations ÷ SMOOTHED monthly income (annual estimate ÷ 12), so a
 * 3-paycheque month doesn't spike the rate. Falls back to the month's actual
 * income only when there's no smoothed estimate yet.
 */
fun savingsRatePercent(
    goalAllocations: Double,
    smoothedMonthlyIncome: Double?,
    actualMonthlyIncome: Double
): Double {
    val denom = smoothedMonthlyIncome?.takeIf { it > 0 } ?: actualMonthlyIncome
    return if (denom > 0) goalAllocations / denom * 100.0 else 0.0
}

/**
 * Format a savings-rate percentage to ONE decimal place (e.g. "30.6", "19.0") —
 * the single display format the rate uses everywhere, so 30.5 vs 30.9 stay
 * distinguishable and every surface reads identically. Caller appends "%".
 */
fun formatSavingsPercentValue(pct: Double): String {
    val tenths = kotlin.math.round(pct * 10).toInt()
    val whole = tenths / 10
    val frac = kotlin.math.abs(tenths % 10)
    return "$whole.$frac"
}

/**
 * Recommendation data for savings rate improvements
 */
data class SavingsRecommendation(
    val message: String,
    val currentRate: Double,
    val targetRate: Double,
    val difference: Double,
    val isOnTrack: Boolean,
    val suggestedMonthlySavings: Double = 0.0
)
